import { Controller } from "@hotwired/stimulus"
import * as bootstrap from "bootstrap"
import { showSystemToast } from "../utils/system_toast"

// Organization settings → Members: each member's department and title chip
// opens the shared picker for that field (organizations/_member_detail_picker),
// moved into the chip's menu the way the handbook's owner picker works. Picking
// a value, typing a new one and pressing Enter, or Clear saves it as JSON and
// relabels the chip in place; the page never reloads.
export default class extends Controller {
    static targets = ["picker"]

    open(event) {
        let chip = event.target.closest(".org-member-chip")
        if (!chip) return

        let picker = this.pickerTargets.find((candidate) => candidate.dataset.field === chip.dataset.field)
        if (!picker) return

        this.chip = chip
        this.picker = picker
        chip.parentElement.querySelector(".org-member-menu").appendChild(picker)
        this.input.value = ""
        this._arrange(chip.closest(".org-members-row").dataset.department)
        this.filter()
    }

    // Once the menu is on screen, not on the next frame: after a save the
    // closing menu hands focus back to its own chip, and a frame-timed focus
    // could land before that and lose to it.
    focus(event) {
        if (this.picker && event.target === this.chip) this.input.focus()
    }

    get input() {
        return this.picker.querySelector(".org-member-search")
    }

    get options() {
        return [...this.picker.querySelectorAll(".org-member-option[data-value]")]
    }

    filter() {
        if (!this.picker) return

        let typed = this.input.value.trim()
        let query = this._fold(typed)
        let shown = 0
        let exact = false
        this.options.forEach((option) => {
            let value = this._fold(option.dataset.value)
            option.hidden = Boolean(query) && !value.includes(query)
            if (!option.hidden) shown += 1
            if (value === query) exact = true
        })
        this.picker.querySelectorAll(".org-member-group").forEach((group) => {
            group.hidden = !group.querySelector(".org-member-option[data-value]:not([hidden])")
        })

        let add = this.picker.querySelector(".org-member-new")
        add.hidden = !typed || exact
        add.textContent = `Use “${typed}”`
        this.picker.querySelector(".org-member-none").hidden = shown > 0 || Boolean(typed)
        this.picker.querySelector(".org-member-clear").hidden = !this.chip.dataset.value
    }

    // Enter takes the exact match, else what was typed, else the only option left.
    keydown(event) {
        if (event.key !== "Enter") return

        event.preventDefault()
        let typed = this.input.value.trim()
        let visible = this.options.filter((option) => !option.hidden)
        let exact = visible.find((option) => this._fold(option.dataset.value) === this._fold(typed))
        if (exact) this.save(exact.dataset.value)
        else if (typed) this.save(typed)
        else if (visible.length === 1) this.save(visible[0].dataset.value)
    }

    choose(event) {
        this.save(event.currentTarget.dataset.value)
    }

    chooseTyped() {
        this.save(this.input.value.trim())
    }

    clear() {
        this.save("")
    }

    async save(value) {
        let chip = this.chip
        let field = chip.dataset.field
        let row = chip.closest(".org-members-row")
        let previous = chip.dataset.value
        bootstrap.Dropdown.getInstance(chip)?.hide()
        this._label(chip, value)
        chip.setAttribute("aria-busy", "true")

        try {
            let response = await fetch(row.dataset.memberDetailsUrl, {
                method: "PATCH",
                headers: {
                    "Content-Type": "application/json",
                    "Accept": "application/json",
                    "X-CSRF-Token": document.querySelector("meta[name='csrf-token']")?.content
                },
                body: JSON.stringify({ [field]: value }),
                credentials: "same-origin"
            })
            let data = await response.json().catch(() => ({}))
            if (!response.ok) throw new Error(data.errors?.join(", ") || "Couldn't save")

            let saved = data[field] || ""
            this._label(chip, saved)
            if (field === "department") row.dataset.department = saved
            this._remember(field, saved, row.dataset.department)
            this._saved(row)
        } catch (error) {
            this._label(chip, previous)
            showSystemToast(error.message || "Couldn't save", { level: "danger" })
        } finally {
            chip.removeAttribute("aria-busy")
        }
    }

    // Titles used in the row's own department first, under their own heading;
    // the chip's current value marked.
    _arrange(department) {
        let near = this.picker.querySelector("[data-kind='near']")
        let rest = this.picker.querySelector("[data-kind='rest']")
        let current = this.chip.dataset.value
        this.options
            .sort((a, b) => a.dataset.value.localeCompare(b.dataset.value))
            .forEach((option) => {
                let used = department && (option.dataset.departments || "").split("|").includes(department)
                ;(used ? near : rest).appendChild(option)
                option.classList.toggle("is-current", option.dataset.value === current)
            })
        near.querySelector(".org-member-group-title").textContent = department ? `Used in ${department}` : ""
    }

    // A value typed for one row is offered to the next.
    _remember(field, value, department) {
        if (!value) return

        let picker = this.pickerTargets.find((candidate) => candidate.dataset.field === field)
        let option = [...picker.querySelectorAll(".org-member-option[data-value]")].find((candidate) => candidate.dataset.value === value)
        if (!option) {
            option = document.createElement("button")
            option.type = "button"
            option.className = "org-member-option"
            option.dataset.value = value
            option.dataset.departments = ""
            option.dataset.action = "member-details#choose"
            option.textContent = value
            picker.querySelector("[data-kind='rest']").appendChild(option)
        }
        if (field === "title" && department) {
            let departments = new Set((option.dataset.departments || "").split("|").filter(Boolean))
            departments.add(department)
            option.dataset.departments = [...departments].join("|")
        }
    }

    _label(chip, value) {
        chip.dataset.value = value
        chip.classList.toggle("is-empty", !value)
        chip.querySelector(".org-member-chip-label").textContent = value || chip.dataset.placeholder
        let field = chip.dataset.field
        chip.setAttribute("aria-label", `${field[0].toUpperCase()}${field.slice(1)} for ${chip.dataset.owner}: ${value || "not set"}. Change`)
    }

    _saved(row) {
        let note = row.querySelector(".org-members-saved")
        if (!note) return

        note.textContent = "Saved"
        note.classList.add("is-shown")
        clearTimeout(note.janeTimer)
        note.janeTimer = setTimeout(() => note.classList.remove("is-shown"), 1600)
    }

    _fold(value) {
        return String(value || "").normalize("NFD").replace(/[̀-ͯ]/g, "").toLowerCase().trim()
    }
}
